import type { EventRow } from './types';

/**
 * 얼린 목업을 읽어 `EventRow` 로 되살린다.
 *
 * ★★★ **양쪽이 같은 파일을 읽는 것이 이식의 계약이다**
 * (`docs/LENS-React-이식-범위산정.md` 1절). Dart 와 JS 는 난수 알고리즘이 달라
 * 생성기를 옮길 수 없으므로, 출력을 얼려 입력을 바이트 단위로 똑같이 만든다.
 */

export interface FixtureMeta {
  fixture_version: number;
  schema_version: string;
  seed: number;
  base_days_ago: number;
  doc_count: number;
  time_field: string;
  time_rule: string;
}

export interface Fixture {
  meta: FixtureMeta;
  events: Array<Record<string, unknown>>;
}

/**
 * 얼린 상대 시각의 기준.
 *
 * ⚠️⚠️ **달력 뺄셈이 아니라 기간 뺄셈이다.** `new Date(y, m, d - 9)` 로 쓰면
 * DST 가 있는 지역에서 Dart 와 한 시간 갈린다 — 한국에는 DST 가 없어
 * **지금은 안 드러난다. 그래서 더 위험하다.**
 *
 * ★ `tools/gen_sample_events.dart` 의 `_baseInstant()` ·
 *   `generated_code/test/lens_golden_dump_test.dart` 의 `baseInstant()` 와
 *   **같은 산식**이어야 한다. 셋 중 하나만 달라져도 값이 조용히 갈린다.
 */
export function baseInstant(baseDaysAgo: number, now = new Date()): Date {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return new Date(midnight.getTime() - baseDaysAgo * 86_400_000);
}

/**
 * JSON 한 줄 → `EventRow`.
 *
 * ⚠️⚠️ **Firestore 게터의 「접힘」을 그대로 흉내 낸다.** 앱은 FF 게터로 읽어서
 * 값이 없으면 `''`/`0`/`false` 가 된다. 여기서 `undefined` 를 흘리면 집계가
 * 조용히 달라진다 — 예컨대 `parseOk` 가 `undefined` 면 `=== false` 가 거짓이라
 * **진행 불가 분자에서 빠진다.**
 *
 * ★ 예외는 `narrationValidated` 하나다. 키가 없으면 `null` 이어야
 *   「검증이 안 돎」과 「검증이 돌고 성공」이 구분된다.
 */
export function rowFromJson(j: Record<string, unknown>, base: Date): EventRow {
  const s = (k: string): string => (typeof j[k] === 'string' ? (j[k] as string) : '');
  const n = (k: string): number => (typeof j[k] === 'number' ? (j[k] as number) : 0);
  const b = (k: string): boolean => j[k] === true;
  const list = (k: string): string[] => (Array.isArray(j[k]) ? (j[k] as string[]) : []);

  return {
    eventName: s('event_name'),
    runId: s('run_id'),
    campaignId: s('campaign_id'),
    chapterId: s('chapter_id'),
    sceneId: s('scene_id'),
    contentVersion: s('content_version'),
    modelVersion: s('model_version'),
    promptVersion: s('prompt_version'),
    appVersion: s('app_version'),
    occurredAt: new Date(base.getTime() + n('occurred_at_offset_ms')),
    exitReason: s('exit_reason'),
    kind: s('kind'),
    dc: n('dc'),
    rollResult: n('roll_result'),
    success: b('success'),
    actionType: s('action_type'),
    parseOk: b('parse_ok'),
    attempts: n('attempts'),
    reason: s('reason'),
    regenerated: b('regenerated'),
    usedFallback: b('used_fallback'),
    durationSec: n('duration_sec'),
    turnCount: n('turn_count'),
    playerId: s('player_id'),
    turnIndex: n('turn_index'),
    endingId: s('ending_id'),
    endingKind: s('ending_kind'),
    costUsd: n('cost_usd'),
    promptTokens: n('prompt_tokens'),
    completionTokens: n('completion_tokens'),
    durationMs: n('duration_ms'),
    fallbackReason: s('fallback_reason'),
    judgmentKind: s('judgment_kind'),
    alertLevel: n('alert_level'),
    pursuitMargin: n('pursuit_margin'),
    playerStates: list('player_states'),
    sceneConditions: list('scene_conditions'),
    sideEffects: list('side_effects'),
    flagId: s('flag_id'),
    flagKind: s('flag_kind'),
    leakedTag: s('leaked_tag'),
    propId: s('prop_id'),
    rejectedAttempts: n('rejected_attempts'),
    viableApproaches: list('viable_approaches'),
    requestedPropId: s('requested_prop_id'),
    // ★ 얼린 목업은 1.4.0 이전이라 이 키가 없다 — 빈 문자열이 맞다.
    noActionReason: s('no_action_reason'),
    schemaVersion: s('schema_version'),
    narrationValidated:
      'narration_validated' in j ? (j['narration_validated'] as boolean) : null,
  };
}

/** 얼린 fixture 전체를 `EventRow[]` 로. */
export function hydrate(fixture: Fixture, now = new Date()): EventRow[] {
  const base = baseInstant(fixture.meta.base_days_ago, now);
  return fixture.events.map((e) => rowFromJson(e, base));
}
